import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { BankAccount, VerificationState } from 'api/TruvApi';
import { BankAccountsStatus } from 'enums/BankAccountsStatus';

export interface DashboardState {
  borrowerPayrollProviderIsDisabled: boolean;
  coBorrowerPayrollProviderIsDisabled: boolean;
  borrowerInformationRequired: boolean;
  coBorrowerInformationRequired: boolean;
  fundingInformationIsRequired: boolean;
  fundingInformationComplete: boolean;
  bankAccountsStatus: BankAccountsStatus;
  bankAccounts: BankAccount[];
  payrollProviderBorrowerVerificationStates: VerificationState[];
  payrollProviderCoBorrowerVerificationStates: VerificationState[];
  isThereNonBorrowingCoOwner: boolean;
  homeOwnershipInformationComplete: boolean;
  borrowerEmploymentInformationComplete: boolean;
  coBorrowerEmploymentInformationComplete: boolean;
  financialAccountAggregationAttemptComplete: boolean;
  fundingInformationErrorMessage: string | null;
  autopayDefaultValue: boolean | null;
  autopaySectionIsRequired: boolean;
  autopaySectionIsCompleted: boolean;
}

const initialState: DashboardState = {
  borrowerPayrollProviderIsDisabled: false,
  coBorrowerPayrollProviderIsDisabled: false,
  borrowerInformationRequired: true,
  coBorrowerInformationRequired: true,
  fundingInformationIsRequired: true,
  fundingInformationComplete: false,
  bankAccountsStatus: BankAccountsStatus.PendingPayrollProvider,
  bankAccounts: [],
  payrollProviderBorrowerVerificationStates: [],
  payrollProviderCoBorrowerVerificationStates: [],
  isThereNonBorrowingCoOwner: false,
  homeOwnershipInformationComplete: false,
  borrowerEmploymentInformationComplete: false,
  coBorrowerEmploymentInformationComplete: false,
  financialAccountAggregationAttemptComplete: false,
  fundingInformationErrorMessage: '',
  autopayDefaultValue: null,
  autopaySectionIsRequired: false,
  autopaySectionIsCompleted: false,
};

const dashboardPortal = createSlice({
  name: 'dashboardPortal',
  initialState,
  reducers: {
    setLoanPortal: (state, { payload }: PayloadAction<Partial<DashboardState>>) => {
      Object.assign(state, payload);
    },
    setVerificationState: (state, { payload }: PayloadAction<{ isBorrower: boolean; verificationId: string }>) => {
      const verificationState = payload.isBorrower
        ? state.payrollProviderBorrowerVerificationStates
        : state.payrollProviderCoBorrowerVerificationStates;

      const currentVerification = verificationState.find((verification) => verification.id === payload.verificationId);

      if (!currentVerification) {
        throw new Error(`Verification state with id ${payload.verificationId} not found`);
      }

      currentVerification.verificationAttemptComplete = true;
    },
  },
});

export const { setLoanPortal, setVerificationState } = dashboardPortal.actions;
export default dashboardPortal.reducer;
